import { type InfiniteData, useMutation } from '@tanstack/react-query';
import { merge } from 'lodash';

import { ShortUrl } from '@air/api';
import { type ListResponse, type ShortUrlResponse } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getShortUrlForCollectionKey } from '~/components/SharePrivateCollectionModal/hooks/queries/useGetShortUrlForCollection';
import { useRevalidatePublicCollection } from '~/hooks/useRevalidatePublicCollection';
import { queryClient } from '~/swr-hooks/queryClient';
import { isShortUrlsListKey } from '~/swr-hooks/shortUrl/useListShortUrls';
import { updateShortUrlInListCache } from './utils';

export type ShortUrlUpdateParams = Parameters<typeof ShortUrl.update>[0];

export type UseUpdateShortUrlMutationParams = {
  collectionId: string;
  updateParams: ShortUrlUpdateParams;
};

/**
 * Helper function to build mutation params for updating a short URL.
 * Merges the existing shortUrl data with the provided updates.
 */
export const buildUpdateShortUrlParams = (
  collectionId: string,
  workspaceId: string,
  shortUrl: ShortUrlResponse,
  updates: Partial<ShortUrlUpdateParams>,
): UseUpdateShortUrlMutationParams => {
  const { id, ...rest } = shortUrl;
  return {
    collectionId,
    updateParams: {
      workspaceId,
      shortId: id,
      ...rest,
      ...updates,
    },
  };
};

export const useUpdateShortUrlMutation = () => {
  const { revalidatePublicCollection } = useRevalidatePublicCollection();

  return useMutation({
    mutationFn: async ({ updateParams }: UseUpdateShortUrlMutationParams) => {
      return ShortUrl.update(updateParams);
    },
    onMutate: async ({ collectionId, updateParams }) => {
      const queryKey = getShortUrlForCollectionKey({ collectionId });

      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) {
          return old;
        }

        return merge({}, old, updateParams);
      });

      return { previousData, updateParams };
    },
    onSuccess: (_data, { collectionId, updateParams }) => {
      const { workspaceId, shortId } = updateParams;
      if (workspaceId && shortId) {
        queryClient.setQueriesData<InfiniteData<ListResponse<ShortUrlResponse>> | undefined>(
          {
            predicate: ({ queryKey }) => isShortUrlsListKey(queryKey) && queryKey[1].workspaceId === workspaceId,
          },
          (old) => (old ? updateShortUrlInListCache(shortId, updateParams, old) : old),
        );
      }

      /**
       * Don't await since this can be slow
       * @see https://air-labs-team.slack.com/archives/CSU3B8B3R/p1699565392614499?thread_ts=1699564602.295969&cid=CSU3B8B3R
       */
      revalidatePublicCollection(collectionId);
    },
    onError: (error, { collectionId }, context) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to update collection short URL',
        metadata: { data: { collectionId } },
      });

      if (context?.previousData) {
        queryClient.setQueryData(getShortUrlForCollectionKey({ collectionId }), context.previousData);
      }
    },
    onSettled: (_data, _error, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: getShortUrlForCollectionKey({ collectionId }) });
    },
  });
};
